import { ApolloError, gql, useLazyQuery, useQuery } from '@apollo/client';
import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { VeevaVaultDocument, VeevaVaultIvaDocumentFilter, VeevaVaultDocumentLimit } from 'generated/maya.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

const GET_ALL_VEEVA_VAULT_IVA_DOCUMENTS = gql`
  query getAllVeevaVaultIVADocument($limit: VeevaVaultDocumentLimit,, $filters: VeevaVaultIvaDocumentFilter) {
    getAllVeevaVaultIVADocument(limit: $limit, filters: $filters) {
      id,
      name,
      documentNumber,
      status,
      executingTeam,
    }
  }
`;

type ResultType = {
  getAllVeevaVaultIVADocument: VeevaVaultDocument[];
};

type UseGetVeevaVaultIVADocument = (limit: VeevaVaultDocumentLimit, filters: VeevaVaultIvaDocumentFilter) => {
  loading: boolean;
  error?: ApolloError;
  data: VeevaVaultDocument[];
};

type UseGetVeevaVaultIVADocumentLazy = () => {
  getAllVeevaVaultIVADocumentLazy: Function;
  loading: boolean;
  error?: ApolloError;
  data: VeevaVaultDocument[];
};

// eslint-disable-next-line max-len
const useGetAllVeevaVaultIVADocument: UseGetVeevaVaultIVADocument = (limit: VeevaVaultDocumentLimit, filters: VeevaVaultIvaDocumentFilter) => {
  const flags = useFlags();
  const { data, loading, error } = useQuery<ResultType>(GET_ALL_VEEVA_VAULT_IVA_DOCUMENTS, {
    fetchPolicy: 'cache-and-network',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
    variables: {
      limit,
      filters,
    },
    skip: !flags.mayaAkma382EnableIvaTacticField,
  });

  if (error) {
    const customContext = extractApolloErrorDetails(error);
    Logger.logError({
      error,
      customContext,
      transactionName: 'getAllVeevaVaultIVADocument.ts - useGetAllVeevaVaultIVADocument',
    });
  }

  return {
    data: data?.getAllVeevaVaultIVADocument || [],
    loading,
    error,
  };
};

export const useGetAllVeevaVaultIVADocumentLazy: UseGetVeevaVaultIVADocumentLazy = () => {
  const [getAllVeevaVaultIVADocumentLazy, { data, loading, error }] = useLazyQuery<ResultType>(GET_ALL_VEEVA_VAULT_IVA_DOCUMENTS, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });

  if (error) {
    const customContext = extractApolloErrorDetails(error);
    Logger.logError({
      error,
      customContext,
      transactionName: 'getAllVeevaVaultIVADocument.ts - useGetAllVeevaVaultIVADocumentLazy',
    });
  }

  return {
    getAllVeevaVaultIVADocumentLazy,
    data: data?.getAllVeevaVaultIVADocument || [],
    loading,
    error,
  };
};

export default useGetAllVeevaVaultIVADocument;
